// Pure mapping of a create-order failure into a friendly, user-facing outcome.
// Extracted from React for unit testing (mirrors the backend validation ->
// exception -> HTTP status table in the ITAM-2583 plan).

export interface MappedSubmitError {
  message: string;
  /** 409 (or idempotent replay): treat as "already created for this consult". */
  is409: boolean;
  /** Best-effort per-line stale-stock messages keyed by itemId. */
  lineErrors?: Record<number, string>;
}

const asText = (data: unknown): string => {
  if (data == null) {
    return "";
  }
  if (typeof data === "string") {
    return data;
  }
  try {
    return JSON.stringify(data);
  } catch {
    return String(data);
  }
};

/**
 * Best-effort: the backend stale-stock error is currently an UNSTRUCTURED
 * message that merely contains the offending item id (no structured field).
 * We parse the first integer as the item id so the FE can attach the error to
 * the matching line; reliable field-level errors need a structured backend
 * response (recorded as a backend dependency).
 */
const parseStockLineErrors = (
  data: unknown
): Record<number, string> | undefined => {
  const text = asText(data);
  const match = text.match(/(\d{1,10})/);
  if (!match) {
    return undefined;
  }
  const itemId = Number(match[1]);
  if (!Number.isFinite(itemId) || itemId <= 0) {
    return undefined;
  }
  return { [itemId]: "Nedovoljno na zalihama, smanjite količinu." };
};

/**
 * The delivery out-of-zone 400. apoteka-back answers it with a plain-text body
 * ("Delivery not allowed from this pharmacy or to selected delivery
 * location!", DeliveryLocationNotInAreaException through the
 * GlobalExceptionHandler): out of zone, missing pharmacy coordinates or a null
 * delivery type. The exception name is kept in case the body ever carries it.
 */
const OUT_OF_ZONE_PATTERN =
  /DeliveryLocationNotInArea|Delivery not allowed from this pharmacy|selected delivery location/i;

export const mapConsultSubmitError = (error: unknown): MappedSubmitError => {
  const err = error as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const status = err?.response?.status;
  const data = err?.response?.data;
  const text = asText(data);

  if (status === 409) {
    return {
      message: "Rezervacija za ovu konsultaciju je već kreirana.",
      is409: true,
    };
  }

  if (status === 400) {
    if (/QuantityExceeded|stock|zalih/i.test(text)) {
      return {
        message:
          "Tražena količina prevazilazi zalihe. Prilagodite količine i pokušajte ponovo.",
        is409: false,
        lineErrors: parseStockLineErrors(data),
      };
    }
    if (OUT_OF_ZONE_PATTERN.test(text)) {
      return {
        message: "Adresa je van zone pomoći pri preuzimanju za izabrani tip.",
        is409: false,
      };
    }
    return {
      message: "Neispravni podaci rezervacije. Proverite unos i pokušajte ponovo.",
      is409: false,
    };
  }

  if (status === 404) {
    return {
      message: "Konsultacija ili artikal nije pronađen.",
      is409: false,
    };
  }

  if (status != null && status >= 500) {
    return {
      message: "Došlo je do greške na serveru. Pokušajte ponovo.",
      is409: false,
    };
  }

  return {
    message:
      err?.message ||
      "Neuspešno kreiranje rezervacije. Pokušajte ponovo.",
    is409: false,
  };
};
